// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  ChartEvent,
  BarController,
  BarElement,
  Point,
  PointElement,
  Legend,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  TimeUnit,
  ActiveElement,
  ChartDataset,
  LegendElement,
  ChartType,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";

import { isSet, roundToDigits } from "shared/utils/utils";
import {
  doKiloConversion,
  getEnergyUnit,
  getPowerUnit,
  hexToRgba,
  COLOR_SELECT,
  roundTimestamp,
  checkCategories,
  getGraphBoundaryDates,
} from "guiutils";
import TrendWindowModel from "model/trendwindow";
import { ActionType, IAction, raiseAction } from "actions";
import { Aggregation } from "graphql/api";
import { IActionTypeZoom } from "actions";
import { TimePeriod, DataState } from "types";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";
import IconZoomFit from "icons/iconZoomFit";
import cssContent from "constants.module.scss";
import ChartBaseComponent, {
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
} from "./ChartBase";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Legend,
  Tooltip,
  zoomPlugin,
);

const DATASET_REFERENCE_IDX_OFFSET = 0;
const DATASET_OVERSHOT_IDX_OFFSET = 1;

// if we have more than this items in the legend, we hide zero values in the tooltip
const NR_TLS_HIDE_ZERO = 10;

interface IEnergyGraphExtProps extends IChartBaseExtProps {
  showLegend: boolean;
}

interface IEnergyGraphProps extends IChartBaseProps {
  graphViewMode: GraphViewMode;
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  loadedAggregation: Aggregation | undefined;
  minTrendTimestamp: number | undefined; // s, available data (cloud)
  maxTrendTimestamp: number | undefined;
}

const mapStateToProps = (state: IState, ownProps: IEnergyGraphExtProps) => {
  const baseProps = baseMapStateToProps(state, ownProps);
  const props: IEnergyGraphProps = {
    ...baseProps,
    graphViewMode: baseProps.selection?.graphViewMode ?? GraphViewMode.BAR,
    xZoom: state.gui.trendPeriod.timePeriod,
    minX: state.gui.trendPeriod.zoomMin,
    maxX: state.gui.trendPeriod.zoomMax,
    loadedAggregation: state.trending.loadedTrendWindow?.aggregation,
    minTrendTimestamp: state.trending.minTrendTimestamp,
    maxTrendTimestamp: state.trending.maxTrendTimestamp,
  };
  return props;
};

interface IEnergyGraphState extends IChartBaseState {
  hiddenData: number[];
}

interface IEnergyGraphDispatch {
  onZooming: (minX?: number, maxX?: number) => void;
  onDetailClick: (timestamp: number, timePeriod: TimePeriod) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnergyGraphDispatch = {
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "trendPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
    onDetailClick: (timestamp: number, timePeriod: TimePeriod) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_TIMESTAMP, timestamp));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
});

// State is never set so we use the 'undefined' type.
class EnergyGraphComponent extends ChartBaseComponent<
  IEnergyGraphProps & IEnergyGraphExtProps & IEnergyGraphDispatch,
  IEnergyGraphState
> {
  state = { hiddenData: [] } as IEnergyGraphState;

  private startTime?: number;
  private endTime?: number;
  private zoomLimitMin = Infinity; // s, Infinity: no limit known
  private zoomLimitMax = -Infinity;
  private data: number[][][] = []; // tariffline array of [timestamp, y] arrays
  private doKiloConvertion = false;
  private refData: number[] = []; // array of ref values, index equals data index
  private aggregation = Aggregation.MONTH;
  private minUnits: TimeUnit = "month";
  private maxValue = 0;
  private zoomLock = false;
  private panLock = false;
  private endDateOffset = 0;

  // the internal data-structure looks like:
  // --> allTariffCircuits hold all available tariffs for that view (from ChartBase)
  // --> this.props.selectedTariffs holds the tariffs selected by the user
  //      via dropDown or via legend (which both shows the current state)
  // --> charts.data.datasets
  //      all tariffs are added to the datasets
  //        the ones were the visible is set to false, no data-points are added
  //        this is needed to show the legend all the time to turn on/off the curves
  //      special indizes:
  //        at the end of the data-sets (this.allTariffs.lenght and this.allTariffs.lenght + 1)
  //        (see constants DATASET_REFERENCE_IDX_OFFSET and DATASET_OVERSHOT_IDX_OFFSET)
  //        the reference and overshoot datasets are added, if only one curve is selected and
  //        reference data is available for the selected curve!!

  protected shouldRedrawGraph(
    nextProps: Readonly<
      IEnergyGraphProps &
        IEnergyGraphExtProps &
        IEnergyGraphDispatch &
        IChartBaseExtProps &
        IChartBaseProps
    >,
    nextState: Readonly<IEnergyGraphState & IChartBaseState>,
    nextContext: any,
  ) {
    return (
      this.props.graphViewMode !== nextProps.graphViewMode ||
      this.state.hiddenData.length !== nextState.hiddenData.length ||
      super.shouldRedrawGraph(nextProps, nextState, nextContext)
    );
  }

  public render() {
    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    if (this.redrawGraph) {
      this.prepareHierarchyData();

      const allTariffCircuits = this.getSelectedTariffCircuits().tlOptions;

      if (allTariffCircuits.length > 0) {
        // only relevant for Hierarchy-Mode:
        // sort available Tariffs to show the ones with matching categories first
        // make sure, the first (SELECTED) entry is again the first one (reference dataset)
        const tmpTariffCircuits = [...allTariffCircuits]
          .filter((_, index) => index > 0)
          .sort((second, first) => {
            const firstHasCategory =
              checkCategories(this.props.selection?.category, first.referenceEntry?.categories) ===
              true;
            const sencondHasCategory =
              checkCategories(this.props.selection?.category, second.referenceEntry?.categories) ===
              true;
            return (
              !firstHasCategory && sencondHasCategory ? -1
              : firstHasCategory && !sencondHasCategory ? 1
              : 0
            );
          });
        this.allTariffCircuits = [allTariffCircuits[0], ...tmpTariffCircuits];
      } else {
        this.allTariffCircuits = [];
      }
    }

    this.preProcessData();

    // check for existing data
    if (
      this.props.dataState !== DataState.SAVE_REQ &&
      this.data.every((curItem) => curItem.length === 0)
    ) {
      return (
        <div id="energygraph" className="graph nodata">
          {this.props.dataState === DataState.SAVE_FAIL ?
            <FormattedMessage id="Failed to load data." />
          : this.props.minX || this.props.maxX ?
            <>
              {this.allTariffCircuits.length > 0 ?
                <FormattedMessage id="No data available. Try reset zooming." />
              : <FormattedMessage id="Please select tariff circuit." />}
              <div
                className="graph-zoom"
                onClick={this.resetZoom.bind(this)}
                title={this.context.formatMessage(messages.resetZoom)}
              >
                <IconZoomFit />
              </div>
            </>
          : this.allTariffCircuits.length > 0 ?
            <FormattedMessage id="No data available." />
          : <FormattedMessage id="Please select tariff circuit." />}
        </div>
      );
    }

    const comp = (
      <div id="energygraph" className="graph">
        {isSet(this.props.minX) || isSet(this.props.maxX) ?
          <div
            className="graph-zoom"
            onClick={this.resetZoom.bind(this)}
            title={this.context.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        : <></>}
        {this.props.dataState === DataState.SAVE_REQ && (
          <div className="loading-spinner-box">
            <div id="loading-spinner"></div>
            <div id="loading-text">
              {this.props.trendLoadingProgress > 0 ?
                <>{Math.round(100 * this.props.trendLoadingProgress)} %</>
              : <></>}
            </div>
          </div>
        )}
        <Chart
          type={this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar"}
          redraw={this.redrawGraph}
          data={this.createGraphData()}
          options={this.createGraphOptions()}
          plugins={[
            {
              id: "customHover",
              afterEvent: (
                chart,
                args: {
                  event: ChartEvent;
                  replay: boolean;
                  changed?: boolean;
                  cancelable: false;
                  inChartArea: boolean;
                },
                opts,
              ) => {
                const evt: ChartEvent = args.event;
                const mouseX = evt.x ?? 0;
                const mouseY = evt.y ?? 0;
                const inArea =
                  mouseX > chart.chartArea.left &&
                  mouseX < chart.chartArea.right &&
                  mouseY > chart.chartArea.top &&
                  mouseY > chart.chartArea.bottom;
                if (
                  !inArea ||
                  (evt.type !== "click" && evt.type !== "mousemove") ||
                  this.props.isDashboard === true
                ) {
                  return;
                }

                const xValue = chart.scales.x.getValueForPixel(evt.x ?? 0) ?? 0;
                const yValue = chart.scales.y.getValueForPixel(evt.y ?? 0) ?? 0;
                const xValid =
                  xValue >= chart.scales.x.min &&
                  xValue <= chart.scales.x.max &&
                  xValue < Date.now();
                const yValid = yValue < chart.scales.y.min;

                const element = document
                  .getElementById("energygraph")
                  ?.getElementsByTagName("canvas")[0];

                if (isSet(element)) {
                  element.style.cursor = xValid && yValid ? "pointer" : "default";
                }

                xValid &&
                  yValid &&
                  evt.type === "click" &&
                  this.props.onDetailClick(Math.round(xValue / 1000 / 900) * 900, this.props.xZoom);
              },
            },
          ]}
        />
      </div>
    );

    return comp;
  }

  private isOnlyOneSelected() {
    // true if
    // - we have only 1 tariff circuit available
    // - we have only 1 selection
    return (
      this.allTariffCircuits.length === 1 || this.props.selection?.tariffCircuitIds?.length === 1
    );
  }

  private preProcessData() {
    // some general data pre-processing
    const { startDate, endDate } = getGraphBoundaryDates(
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.xZoom,
    );
    // zooming may exceed the selected period: then take all loaded data, the axis clips it
    const isZoomed = isSet(this.props.minX) || isSet(this.props.maxX);
    const { usedTimestamps } = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      isZoomed ? TimePeriod.ALL : this.props.xZoom,
    );

    // allow zooming out to all available data, but at least to the selected period
    this.zoomLimitMin = Math.min(startDate ?? Infinity, this.props.minTrendTimestamp ?? Infinity);
    this.zoomLimitMax = Math.max(endDate ?? -Infinity, this.props.maxTrendTimestamp ?? -Infinity);
    this.startTime = this.props.minX ? this.props.minX / 1000 : startDate;
    this.endTime = this.props.maxX ? this.props.maxX / 1000 : endDate;
    // prefer the aggregation of the loaded data (cloud), which considers zooming
    // the fallback serves the local controller, which has no (licensing) aggregation boundary
    this.aggregation =
      this.props.loadedAggregation ??
      TrendWindowModel.getAggregation(
        this.props.timePeriod,
        this.startTime,
        this.endTime,
        undefined,
      );
    this.endDateOffset = [Aggregation.MONTH, Aggregation.DAY].includes(this.aggregation) ? 1 : 0;

    this.minUnits = TrendWindowModel.getTimeUnit(this.aggregation);
    this.data = [];
    this.refData = [];
    // check if only 1 tariff is selected
    const hasReference = this.isOnlyOneSelected() && this.props.trendMember === "power";

    // loop through sorted tariff circuits
    const oldMax = this.maxValue;
    this.maxValue = 0;
    const tsMaxValue: (number | undefined)[] = [];

    this.allTariffCircuits.forEach((curTariff) => {
      const curCircuit = curTariff.referenceEntry;
      const tariffIndex = this.props.tariffCircuits.findIndex((entry) => entry === curCircuit);
      // is this tariff line active and selected?
      if (curCircuit?.webActive) {
        // not all timestamps may have a value for this TL associated, therefore we cannot use map()
        const dataset: number[][] = [];
        usedTimestamps.forEach((curTimestamp, tsIdx) => {
          let value: number | undefined;
          let refValue: number | undefined;
          // find data row, either matching timestamp or matching rounded timestamp
          const dataRow = this.props.trendData.find(
            (curItem) =>
              roundTimestamp(curItem.timestamp) === curTimestamp &&
              curItem.tlId === (curCircuit.id ?? tariffIndex),
          );
          if (dataRow && isSet(dataRow[this.props.trendMember])) {
            value = dataRow[this.props.trendMember]!;
          }

          // determine maximum: accumulate all values of each timestamp
          if (this.startTime! < curTimestamp && this.endTime! > curTimestamp) {
            if (this.props.graphViewMode === GraphViewMode.BAR) {
              tsMaxValue[tsIdx] = (tsMaxValue[tsIdx] ?? 0) + (value ?? 0);
            } else {
              if (isSet(value) && (!isSet(tsMaxValue[tsIdx]) || tsMaxValue[tsIdx]! < value)) {
                tsMaxValue[tsIdx] = value;
              }
            }
          }

          // for power values we also add reference data; power values will only consist of one series
          if (hasReference && isSet(dataRow?.targetpower)) {
            refValue = dataRow.targetpower;
            if (!isSet(tsMaxValue[tsIdx]) || refValue > tsMaxValue[tsIdx]!) {
              tsMaxValue[tsIdx] = refValue;
            }
          }

          // we need to add even inexisting values (on bar-charts only!) if we show multiple data sets
          // otherwise the bars will have different widths which looks strange
          if (
            isSet(value) ||
            (this.props.graphViewMode !== GraphViewMode.LINE && !this.isOnlyOneSelected())
          ) {
            dataset.push([curTimestamp, value ?? NaN]);
            // only add reference-data if the current tariff
            // is the only-one selected
            if (hasReference /*&& dataIdx === tariffIdx*/) {
              this.refData.push(refValue ?? NaN);
            }
          }
        });
        this.data.push(dataset);
      }
    });

    // derive maximum
    this.maxValue = tsMaxValue.reduce(
      (prev, cur) => (!isSet(cur) || (isSet(prev) && prev > cur) ? prev : cur),
      0,
    )!;

    // check whether we need to multiply everything by 10000
    this.doKiloConvertion =
      doKiloConversion(this.maxValue) && ["energy", "power"].includes(this.props.trendMember);
    if (this.doKiloConvertion) {
      this.data.forEach((curSeries) => {
        curSeries.forEach((curData) => {
          curData[1] *= 1000;
        });
      });
      this.refData = this.refData.map((curItem) => 1000 * curItem);
      this.maxValue *= 1000;
    }
    // ensure to have at least 2 data sets in bar graphs to avoid the problem with bars being too
    // thick if only a single data item is shown
    if (usedTimestamps.length === 1 && this.props.graphViewMode !== GraphViewMode.LINE) {
      // append a zero data item
      const startDate = usedTimestamps[0];
      const daysToShift =
        this.props.xZoom === TimePeriod.DAY ? 1 / 24
        : this.props.xZoom === TimePeriod.WEEK ? 1
        : this.props.xZoom === TimePeriod.MONTH ? 7
        : /* this.props.xZoom === TimePeriod.YEAR */ 30;
      const newDate = startDate + daysToShift * 24 * 3600;
      // add it to each tariff line
      this.data.forEach((curItem) => curItem.push([newDate, 0]));
    }

    if (this.maxValue === 0) {
      this.maxValue = oldMax;
    }
    this.maxValue = this.maxValue * 1.05;
  }

  private getDataUnit(seriesIndex?: number) {
    const entryId = this.allTariffCircuits[seriesIndex ?? 0]?.entryId;
    const tlId = isSet(entryId) ? (entryId.at(-1) ?? 0) : 0;
    const tariffCircuit = this.props.tariffCircuits.find(
      (curItem, curIndex) => (curItem.id ?? curIndex) === tlId,
    );
    let unit: string | undefined = "";
    // if we did not specify the tariff circuit, we take the very first one
    if (this.props.trendMember === "energy") {
      unit = getEnergyUnit(tariffCircuit?.unit, this.doKiloConvertion);
    } else if (this.props.trendMember === "power") {
      unit = getPowerUnit(tariffCircuit?.unit, this.doKiloConvertion);
    } else if (this.props.trendMember === "costs") {
      unit = "EUR";
    }
    return unit ?? "";
  }

  private createGraphData() {
    const data: ChartData<"bar" | "line", ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    // do not provide data while still loading
    if (this.props.dataState === DataState.SAVE_REQ) {
      return data;
    }

    const hasRefData =
      this.isOnlyOneSelected() &&
      this.refData.length > 0 &&
      this.refData.filter((val) => !isNaN(val)).length > 0;

    // prepare all graph-data (for legend at least!)
    this.data.forEach((curData, curSeriesIndex) => {
      const points: ScatterDataPoint[] = curData.map((curItem) => {
        return {
          x: curItem[0] * 1000,
          y: curItem[1],
        };
      });
      const isSelectedHierarchy =
        curSeriesIndex === 0 &&
        this.props.isHierarchyViewMode === true &&
        this.allTariffCircuits.some((entry) => isSet(entry.referenceEntry?.parent));
      const showLinePoints = this.props.graphViewMode === GraphViewMode.BAR && isSelectedHierarchy;
      const color = this.allTariffCircuits[curSeriesIndex].color;

      const dataset: ChartDataset<"bar" | "line", Point[]> = {
        label: this.allTariffCircuits[curSeriesIndex]?.referenceEntry?.name ?? "data",
        data: points,
        type: this.props.graphViewMode === GraphViewMode.LINE || showLinePoints ? "line" : "bar",
        borderColor: isSelectedHierarchy ? cssContent.POWERPROGRESS_GRAYED_OUT : color,
        backgroundColor: isSelectedHierarchy ? cssContent.GRAPH_GRAYED_OUT : color,
        hidden: this.state.hiddenData.includes(curSeriesIndex),
        barThickness: isSelectedHierarchy ? undefined : "flex",
        stack: `${isSelectedHierarchy}`,
        showLine: !showLinePoints,
        pointStyle: showLinePoints ? "point" : undefined,
        pointRadius: showLinePoints ? 2.5 : undefined,
        pointBorderWidth: showLinePoints ? 1 : undefined,
      };
      data.datasets.push(dataset);
    });

    // provide a solution for bar graphs with only one single data element, which has very high thickness
    if (hasRefData) {
      const points: ScatterDataPoint[] = [];
      const overshootPoints: ScatterDataPoint[] = [];
      const refPoints: ScatterDataPoint[] = [];
      // get index from from selected single-line
      const dataIdx = 0; //this.allTariffCircuits.findIndex((item) => item.visible === true);

      // add all data points sequentially
      this.data[0].forEach((curItem, ind) => {
        const curTimestamp = curItem[dataIdx] * 1000;
        const curData = curItem[1];
        let curRef: number | undefined;
        if (this.refData.length > ind) {
          curRef = this.refData[ind];
        }

        // data point: in bar chart reduced to target value, as it gets extended with overshoot
        const y =
          curRef === undefined || this.props.graphViewMode === GraphViewMode.LINE ?
            curData
          : Math.min(curData, curRef);
        points.push({
          x: curTimestamp,
          y,
        });

        // target value
        refPoints.push({
          x: curTimestamp,
          y: curRef ?? 0,
        });
        // overshoot; not used in line graph but created anyway
        overshootPoints.push({
          x: curTimestamp,
          y: Math.max(curData - (curRef ?? curData), 0),
        });
      });

      // exchange dataset!
      const color =
        this.state.hiddenData.includes(this.allTariffCircuits[dataIdx].referenceEntry?.id ?? -1) ?
          cssContent.GRAPH_GRAYED_OUT
        : this.allTariffCircuits[dataIdx].color;
      data.datasets[dataIdx] = {
        label: this.allTariffCircuits[dataIdx].referenceEntry?.name ?? "data",
        data: points,
        hidden: false,
        type: this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar",
        borderColor: color,
        backgroundColor: color,
      };

      // reference points for LINE and BAR graph
      data.datasets[this.allTariffCircuits.length + DATASET_REFERENCE_IDX_OFFSET] = {
        label: "Reference",
        data: refPoints,
        type: "line",
        hidden: false,
        //steppedLine: true,
        pointRadius: 0,
        borderColor: cssContent.GRAPH_COLOR_TARGET,
      };
      // overshoot only for BAR graph
      if (this.props.graphViewMode !== GraphViewMode.LINE) {
        data.datasets[this.allTariffCircuits.length + DATASET_OVERSHOT_IDX_OFFSET] = {
          label: "Overshoot",
          data: overshootPoints,
          type: "bar",
          hidden: false,
          borderColor: cssContent.GRAPH_COLOR_OVERSHOOT,
          backgroundColor: cssContent.GRAPH_COLOR_OVERSHOOT,
        };
      }
    }

    return data;
  }

  private createGraphOptions() {
    const isLegendTop = this.allTariffCircuits.length <= NR_TLS_HIDE_ZERO;
    const options: ChartOptions<"bar" | "line" | "scatter"> = {
      scales: {
        x: {
          type: "time",
          time: {
            //unit: this.useBarChart ? "hour" : "day",
            minUnit: this.minUnits,
            displayFormats: {
              minute: this.context.formatMessage(messages.graphFormatAxisMinute),
              hour: this.context.formatMessage(messages.graphFormatAxisHour),
              day: this.context.formatMessage(messages.graphFormatAxisDay),
            },
          },
          min: this.startTime ? this.startTime * 1000 : undefined,
          // set max date one month earlier at year view?
          max: this.endTime ? this.endTime * 1000 - this.endDateOffset : undefined,
          stacked: this.props.graphViewMode === GraphViewMode.LINE ? false : true,
          grid: {
            offset: false,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: this.getDataUnit(),
          },
          stacked: this.props.graphViewMode === GraphViewMode.LINE ? false : true,
        },
      },
      datasets: {
        line: {
          pointRadius: 2, // default 3
          tension: 0.4, // line smoothness, default 0
          fill: false,
        },
        bar: {
          barThickness: "flex",
        },
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      plugins: {
        legend: {
          display:
            this.props.showLegend &&
            (window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop),
          position: isLegendTop ? "top" : "right",
          labels: {
            // only show legend of real tariffs (not added REFERENCE or OVERSHOOT (if any))
            filter: (item: LegendItem, data: ChartData) =>
              isSet(item.datasetIndex) ? item.datasetIndex < this.allTariffCircuits.length : false,

            generateLabels: (chart) => {
              return this.allTariffCircuits.map((tl, idx) => {
                const isSelectedHierarchy =
                  idx === 0 &&
                  this.props.isHierarchyViewMode === true &&
                  this.allTariffCircuits.some((entry) => isSet(entry.referenceEntry?.parent));
                return {
                  text: tl.text,
                  fillStyle: isSelectedHierarchy ? cssContent.POWERPROGRESS_GRAYED_OUT : tl.color,
                  strokeStyle: isSelectedHierarchy ? cssContent.GRAPH_GRAYED_OUT : tl.color,
                  hidden: this.state.hiddenData.includes(idx),
                  datasetIndex: 0,
                  index: idx,
                } as LegendItem;
              }) as LegendItem[];
            },
          },
          onClick: (evt, legendItem, legend) =>
            this.legendItemClick(evt, legendItem, legend as LegendElement<ChartType>),
        },
        title: {
          text:
            this.allTariffCircuits.length === 1 ?
              this.allTariffCircuits[0].referenceEntry?.name
            : undefined,
        },
        tooltip: {
          enabled: true,
          mode: "point",
          position: "nearest", // show tooltip on average value, not at mouse cursor
          callbacks: {
            title: (tooltipItems: TooltipItem<"bar" | "line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x ?? 0);
              const showTime = [Aggregation.NONE, Aggregation.HOUR].includes(this.aggregation);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: this.aggregation !== Aggregation.MONTH ? "numeric" : undefined,
                hour: showTime ? "numeric" : undefined,
                minute: showTime ? "numeric" : undefined,
              };
              return date.toLocaleString(this.context.locale, localeOptions);
            },
            label: (tooltipItem) => this.getTooltipLabel(tooltipItem),
          },
        },
        // the controller offers fixed periods only, without zooming and panning
        zoom: {
          pan: {
            enabled: IS_CLOUD,
            mode: "x",
            modifierKey: "shift",
            onPanStart: (context) => this.panStartCallback(context.chart, context.point),
            onPanComplete: (context) => {
              this.panCompleteCallback();
              // store the panned range like a zoom, which also reloads data if needed
              this.zoomCallback(context.chart);
            },
            onPanRejected: () => this.panCompleteCallback(),
          },
          zoom: {
            mode: "x",
            wheel: {
              enabled: IS_CLOUD,
            },
            drag: {
              enabled: IS_CLOUD,
              borderColor: hexToRgba(COLOR_SELECT, 0.7),
              backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
            },
            pinch: {
              enabled: IS_CLOUD,
            },
            onZoom: (context) => this.zoomCallback(context.chart),
            onZoomStart: (context) => this.zoomCheck(context.chart, context.point),
            onZoomComplete: () => this.zoomCompleteCallback(),
            onZoomRejected: () => this.zoomCompleteCallback(),
          },
          // explicit limits; "original" is not stable as the plugin re-captures it whenever our
          // axis options differ from its own (e.g. by endDateOffset)
          limits: {
            x: {
              min: isFinite(this.zoomLimitMin) ? this.zoomLimitMin * 1000 : "original",
              max: isFinite(this.zoomLimitMax) ? this.zoomLimitMax * 1000 : "original",
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      animation: {
        duration: 0,
      },
      onClick: (event, elements, chart) => this.onGraphClick(elements),
      onHover: (event, elements, chart) => this.checkMouse(elements),
    };

    return options;
  }

  private getTooltipLabel(tooltipItem: TooltipItem<"bar" | "line">) {
    // hide zero values if the legend is large enough
    // or for reference/overshoot values (they are attached at the end of the selectedTariffs)
    if (
      (this.allTariffCircuits.length > NR_TLS_HIDE_ZERO && tooltipItem.parsed.y === 0) ||
      tooltipItem.datasetIndex > this.allTariffCircuits.length
    ) {
      return "";
    }
    // special handling for power tooltips
    if (this.props.trendMember === "power") {
      return this.getPowerTooltipLabel(tooltipItem);
    }
    // non-power tooltips
    const propName = tooltipItem.dataset.label ? `${tooltipItem.dataset.label}: ` : "";
    const value = roundToDigits(tooltipItem.parsed.y, 2);
    const unit = this.getDataUnit(tooltipItem.datasetIndex);
    return ` ${propName}${value} ${unit}`;
  }

  private getPowerTooltipLabel(tooltipItem: TooltipItem<"bar" | "line">) {
    // for power values, we need to add the overflow bar
    // we need to sum the data and increased data sets to get the total power
    // @@@ Marc -> need rework --> check if current item index > max-available data!
    //    if one higher -> REFERENCE-VALUE; if 2 higher -> OVERSHOOT-VALUE
    const datasets = tooltipItem.chart.data.datasets;
    const showSingleItem = datasets.length > this.allTariffCircuits.length;
    let value = tooltipItem.parsed.y;
    const label = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label;
    let dataIdx = tooltipItem.datasetIndex;

    // check if we should show special entries for tool-tip if reference-values
    // might be available
    if (showSingleItem) {
      // get index from from selected single-line
      dataIdx = 0; //this.allTariffCircuits.findIndex((item) => item.visible === true);
      // get value of the tariff to show (this is the already limited value)
      const dataVal =
        (datasets[dataIdx]?.data?.[tooltipItem.dataIndex] as ScatterDataPoint | undefined)?.y ?? 0;

      // get overshoot-value
      const increasedVal =
        (showSingleItem ?
          (datasets[this.allTariffCircuits.length + DATASET_OVERSHOT_IDX_OFFSET]?.data?.[
            tooltipItem.dataIndex
          ] as ScatterDataPoint | undefined)
        : undefined
        )?.y ?? 0;

      // check if the current tool-tip label is the main-value of the limit or overshoot value
      if (tooltipItem.datasetIndex < this.allTariffCircuits.length) {
        // if the real value should get shown:
        // -> sum limited-value and overshoot
        value = dataVal + increasedVal;
      } else {
        return "";
      }
    }

    // make sure property name is avialble
    const propName = label ? `${label}: ` : "";
    // show prepared name and value -> always use the data-unit of the selected tariff
    return ` ${propName}${roundToDigits(value, 2)} ${this.getDataUnit(dataIdx)}`;
  }

  private zoomCallback(chart: ChartJS) {
    const axis = chart.scales.x;
    this.props.onZooming(axis.min, axis.max);
  }

  private resetZoom() {
    this.props.onZooming();
  }

  private zoomCheck(chart: ChartJS, point: Point) {
    this.panLock =
      !this.zoomLock &&
      point.x !== null &&
      point.y !== null &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;
    return this.panLock;
  }

  private zoomCompleteCallback() {
    this.panLock = false;
  }

  private panStartCallback(chart: ChartJS, point: Point) {
    // panning beyond the selected period is fine as well, limits are the available data
    this.zoomLock =
      !this.panLock &&
      point.x !== null &&
      point.y !== null &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;

    return this.zoomLock;
  }

  private panCompleteCallback() {
    this.zoomLock = false;
  }

  private legendItemClick(
    evt: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement<ChartType>,
  ) {
    const index = legendItem.index ?? 0;
    const wasHidden = !this.state.hiddenData.includes(index);
    const mouseEvent = evt.native as PointerEvent;
    const itemCnt = legend.chart.data.datasets.length;

    const hiddenData =
      mouseEvent?.shiftKey ?
        wasHidden === false || Math.abs(this.state.hiddenData.length - itemCnt) !== 1 ?
          Array.from(Array(itemCnt).keys()).filter((idx) => idx !== index)
        : []
      : [
          ...this.state.hiddenData.filter((di) => di !== index),
          ...(wasHidden === true ? [index] : []),
        ];

    this.setState({ hiddenData });

    return;
  }

  private onGraphClick(elements: ActiveElement[]) {
    // point selected?
    const activePoint = elements.length ? elements[0] : undefined;
    const timestamp = this.data[activePoint?.datasetIndex ?? 0]?.[activePoint?.index ?? 0]?.[0];
    if (isSet(activePoint) && isSet(timestamp)) {
      // click on data item
      this.props.onDetailClick(timestamp, this.props.xZoom);
    }
  }

  private checkMouse(elements: ActiveElement[]) {
    // point hovered -> show different mouse-pointer
    const activePoint = elements.length ? elements[0] : undefined;
    const timestamp = this.data[activePoint?.datasetIndex ?? 0]?.[activePoint?.index ?? 0]?.[0];

    const element = document.getElementById("energygraph")?.getElementsByTagName("canvas")[0];

    if (isSet(element)) {
      element.style.cursor = isSet(activePoint) && isSet(timestamp) ? "pointer" : "default";
    }
  }
}

const EnergyGraph = connect(mapStateToProps, mapDispatchToProps)(EnergyGraphComponent);

export default EnergyGraph;
